import React, { useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { IconBroom } from '@pkg/assets/icons';
import { CurrencyIcon, FieldNumber, type FieldNumberProps } from '@pkg/components';
import { type BaseCurrencyType } from '@pkg/types';

import {
    CloseAction,
    CloseActionLabel,
    CloseActionWrapper,
    CurrencyIconWrapper,
    IconWrapper,
    OpenAction,
    OpenActionWrapper,
    StyledSweepTicker,
    SweepAction,
    SweepActionCurrency,
    SweepActionLabel,
    SweepControls,
    Widget,
} from './SweepTicker.style';
import { tokens } from './SweepTicker.tokens';

export interface SweepTickerProps {
    initialValue?: FieldNumberProps['initialValue'];
    chain?: BaseCurrencyType;
    sweepCount?: number;
    totalPrice?: number;
    max?: FieldNumberProps['max'];
    darkMode?: boolean;
    disabled?: FieldNumberProps['disabled'];
    // Passing the `onChange` callback to both "change" & "submit".
    onChange: FieldNumberProps['onSubmit'];
    onSweep?: () => void;
}

export function SweepTicker({
    initialValue = 0,
    chain = 'eth',
    sweepCount = 0,
    totalPrice = 0,
    max = 100,
    darkMode = false,
    disabled = false,
    onChange,
    onSweep,
}: SweepTickerProps) {
    const [translate] = useTranslation('marketplace');
    const [open, setOpen] = useState(false);

    const closeActionMarkup = open ? (
        <CloseActionWrapper>
            <CloseAction ariaLabel={translate('sweep-ticker-close-cta-a11y')} onAction={() => setOpen(false)}>
                <CloseActionLabel>{translate('sweep-ticker-close-cta')}</CloseActionLabel>
            </CloseAction>
        </CloseActionWrapper>
    ) : null;

    const sweepControlsMarkup = open ? (
        <SweepControls>
            <FieldNumber
                autoFocus
                disabled={disabled}
                min={0}
                max={max}
                size={tokens.fieldNumber.size}
                initialValue={initialValue}
                // `onBlur` required in order to satisfy the
                // "undefined to last valid value" condition.
                onBlur={onChange}
                onChange={onChange}
                onSubmit={onChange}
            />

            <SweepAction
                disabled={disabled || sweepCount < 1}
                ariaLabel={translate('sweep-ticker-buy-cta-a11y')}
                onAction={onSweep}
            >
                <SweepActionLabel>{translate('sweep-ticker-buy-cta')}</SweepActionLabel>

                <SweepActionCurrency>
                    <CurrencyIconWrapper>
                        <CurrencyIcon currency={chain} />
                    </CurrencyIconWrapper>
                </SweepActionCurrency>

                <SweepActionLabel>{totalPrice}</SweepActionLabel>
            </SweepAction>
        </SweepControls>
    ) : null;

    return (
        <StyledSweepTicker $darkMode={darkMode}>
            <AnimatePresence>{closeActionMarkup}</AnimatePresence>

            <Widget $open={open}>
                <OpenActionWrapper>
                    <OpenAction
                        disabled={open}
                        ariaLabel={translate('sweep-ticker-toggle-cta-a11y')}
                        onAction={() => setOpen(true)}
                    >
                        <IconWrapper>
                            <IconBroom />
                        </IconWrapper>
                    </OpenAction>
                </OpenActionWrapper>

                <AnimatePresence>{sweepControlsMarkup}</AnimatePresence>
            </Widget>
        </StyledSweepTicker>
    );
}
